import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { closeResourceEditModal, openResourceEditModal } from 'js/actions/app/resourceEditModal';
import useAppDispatch from 'js/hooks/useAppDispatch';
import ModalSourceForm from 'js/components/data_sources/add_ds_form/ModalSourceForm';
import ModalELTScriptRunnerSourceForm from 'js/components/data_sources/add_ds_form/ModalELTScriptRunnerSourceForm';
import ModalNexsetForm from 'js/components/sendData/ModalNexsetForm';
import ModalDestinationForm from 'js/components/destination/ModalDestinationForm';
import {
  RESOURCE_TYPE_SINK,
  RESOURCE_TYPE_SOURCE,
  RESOURCE_TYPE_DATA_SET,
  RESOURCE_TYPE_CREDENTIALS,
} from 'js/constants/resourceTypes';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { isSqlAutomation } from 'js/lib/ResourceUtils';
import { useSqlAutomation } from 'new/hooks/useFeatureFlags';
import ModalCredentialForm from '../credentials/ModalCredentialForm';
import { Action, ResourceEditContext } from './ResourceEditContext';

type Props = {
  children: React.ReactNode;
  onComplete?: () => void;
};

const isResourceEditModalAction = (action: Action | null): action is Action =>
  !!action && (action.type === 'create' || (action.type === 'edit' && !!action.id));

export default function ResourceEditProvider({ children, onComplete }: Props) {
  const dispatch = useAppDispatch();
  const [action, setAction] = useState<Action | null>(null);
  const isSqlAutomationEnabled = useSqlAutomation();

  const sourceId =
    action?.resource === RESOURCE_TYPE_SOURCE && action?.type === 'edit' && action?.id
      ? action.id
      : null;
  const source = useSelector((state: any) =>
    sourceId ? state.entities?.sources?.[sourceId] : null,
  );
  const vendors = useSelector((state: any) => state.entities?.vendors ?? {});

  const isSqlRunnerSource =
    action?.resource === RESOURCE_TYPE_SOURCE &&
    (action?.type === 'edit' && action?.id
      ? isSqlAutomation(source, vendors)
      : action?.queryParams?.selectedConnection === CONNECTOR_CODE_SQL_RUNNER);

  const useNewSqlRunnerModal = isSqlRunnerSource && isSqlAutomationEnabled;
  const resourceFlowType = action?.flowType || source?.flow_type || 'streaming';

  const onLocalComplete = useCallback(
    (data: any) => {
      action?.onComplete?.(data);
      onClose();
      onComplete?.();
    },
    [action, onComplete],
  );

  const onClose = useCallback(() => {
    setAction(null);
    dispatch(closeResourceEditModal());
  }, [dispatch]);

  const handledSetAction = useCallback(
    (nextAction: Action) => {
      setAction(nextAction);
      if (isResourceEditModalAction(nextAction)) {
        dispatch(openResourceEditModal());
      }
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(closeResourceEditModal());
    };
  }, [dispatch]);

  return (
    <ResourceEditContext.Provider
      value={{ setAction: handledSetAction, queryParams: action?.queryParams }}
    >
      {action?.resource === RESOURCE_TYPE_SOURCE && useNewSqlRunnerModal ? (
        <ModalELTScriptRunnerSourceForm
          id={action?.id}
          open={!!(action?.resource === RESOURCE_TYPE_SOURCE && useNewSqlRunnerModal)}
          onClose={onClose}
          parentDataSetId={action?.parentDataSetId}
          onDismiss={onLocalComplete}
        />
      ) : (
        <ModalSourceForm
          id={action?.id}
          open={
            action?.resource === RESOURCE_TYPE_SOURCE &&
            (action?.type === 'create' || (action?.type === 'edit' && !!action?.id)) &&
            !useNewSqlRunnerModal
          }
          onClose={onClose}
          flowType={resourceFlowType}
          parentDataSetId={action?.parentDataSetId}
          queryParams={action?.queryParams}
          onDismiss={onLocalComplete}
        />
      )}
      <ModalNexsetForm
        open={
          action?.resource === RESOURCE_TYPE_DATA_SET &&
          (action?.type === 'create' || (action?.type === 'edit' && !!action?.id))
        }
        id={action?.id}
        isEditMode={action?.type === 'edit'}
        onClose={onClose}
        onDismiss={onLocalComplete}
      />
      <ModalDestinationForm
        open={
          action?.resource === RESOURCE_TYPE_SINK &&
          (action?.type === 'create' || (action?.type === 'edit' && !!action?.id))
        }
        id={action?.id}
        nexsetId={action?.nexsetId}
        isEditMode={action?.type === 'edit'}
        flowType={action?.flowType || 'streaming'}
        queryParams={action?.queryParams}
        onClose={onClose}
        onDismiss={onLocalComplete}
      />
      <ModalCredentialForm
        id={action?.id}
        open={
          action?.resource === RESOURCE_TYPE_CREDENTIALS &&
          (action?.type === 'create' || (action?.type === 'edit' && !!action?.id))
        }
        isCreateMode={action?.type === 'create'}
        onDismiss={onLocalComplete}
        onClose={onClose}
      />
      {children}
    </ResourceEditContext.Provider>
  );
}
